import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  UserGroupIcon,
  ClockIcon,
  CalendarDaysIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  PlusIcon,
  AdjustmentsHorizontalIcon,
  ArrowLeftIcon,
  UserPlusIcon, 
  UserMinusIcon,
} from '@heroicons/react/24/outline';
import { Clock, BarChart3 } from 'lucide-react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import ActivityLogs from '../components/ActivityLogs';
import Reports from '../components/Reports';
import { getPageConfig } from '../utils/pageConfigs';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

// --- START: MOCK DATA ---
const attendanceData = [
  { id: 1, name: 'Rohan Mehta', employeeId: 'EMP001', status: 'Present', punchIn: '09:00 AM', punchOut: '05:30 PM' },
  { id: 2, name: 'Ananya Roy', employeeId: 'EMP002', status: 'On Leave', punchIn: null, punchOut: null },
  { id: 3, name: 'Vikram Singh', employeeId: 'EMP003', status: 'Present', punchIn: '09:15 AM', punchOut: null },
  { id: 4, name: 'Divya Sharma', employeeId: 'EMP004', status: 'Absent', punchIn: null, punchOut: null },
  { id: 5, name: 'Kavya Pillai', employeeId: 'EMP005', status: 'Present', punchIn: '09:05 AM', punchOut: '05:45 PM' },
  { id: 6, name: 'Rahul Joshi', employeeId: 'EMP006', status: 'Present', punchIn: '08:55 AM', punchOut: null },
  { id: 7, name: 'Nisha Reddy', employeeId: 'EMP007', status: 'Present', punchIn: '09:20 AM', punchOut: '05:20 PM' },
  { id: 8, name: 'Arjun Das', employeeId: 'EMP008', status: 'On Leave', punchIn: null, punchOut: null },
];
// --- END: MOCK DATA ---

const AttendanceManagement = ({ onBack = () => console.log('Back to Dashboard triggered.') }) => {
  // Switched from moment() to native Date()
  const [currentTime, setCurrentTime] = useState(new Date()); 
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Get page-specific configurations
  const pageConfig = getPageConfig('Attendance Management');

  useEffect(() => {
    // Switched to using new Date() for real-time updates
    const timerId = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timerId);
  }, []);

  // Format the time using native Date and Intl API
  const formattedTime = currentTime.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit', 
    second: '2-digit', 
    hour12: true 
  });

  // Format the date using native Date and Intl API
  const formattedDate = currentTime.toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });


  const totalEmployees = attendanceData.length;
  const presentCount = attendanceData.filter(e => e.status === 'Present').length;
  const absentCount = attendanceData.filter(e => e.status === 'Absent').length;
  const onLeaveCount = attendanceData.filter(e => e.status === 'On Leave').length;

  const chartData = {
    labels: ['Present', 'Absent', 'On Leave'],
    datasets: [{
      data: [presentCount, absentCount, onLeaveCount],
      backgroundColor: ['#4ade80', '#ef4444', '#f59e0b'],
      borderColor: ['#fff', '#fff', '#fff'],
      borderWidth: 2,
    }],
  };

  const filteredData = attendanceData.filter(employee =>
    employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.employeeId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'Present':
        return 'text-green-600 bg-green-100';
      case 'On Leave':
        return 'text-yellow-600 bg-yellow-100';
      case 'Absent':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-100 to-purple-200 p-8 text-gray-800">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto mb-8"
      >
        <div className="flex items-center mb-4">
            {/* Back Button Implementation */}
            <button
                onClick={onBack}
                className="p-2 mr-4 bg-white rounded-full hover:bg-gray-200 transition text-gray-700 shadow-md"
                aria-label="Back to Dashboard"
            >
                <ArrowLeftIcon className="h-6 w-6" />
            </button>
            <h1 className="text-4xl font-extrabold text-purple-800">Attendance Management</h1>
        </div>
        <p className="text-xl font-light text-purple-600 mt-2">
          Track and manage employee attendance in real-time.
        </p>
      </motion.div>

      {/* Overview Cards Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 max-w-7xl mx-auto">
        <motion.div variants={cardVariants} initial="hidden" animate="visible">
          <div className="bg-white p-6 rounded-3xl shadow-lg flex items-center space-x-4">
            <div className="p-3 rounded-full bg-blue-100">
              <UserGroupIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h3 className="text-3xl font-extrabold text-gray-900">{totalEmployees}</h3>
              <p className="text-sm text-gray-500">Total Employees</p>
            </div>
          </div>
        </motion.div>
        <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
          <div className="bg-white p-6 rounded-3xl shadow-lg flex items-center space-x-4">
            <div className="p-3 rounded-full bg-green-100">
              <UserPlusIcon className="h-8 w-8 text-green-600" /> {/* Corrected Icon */}
            </div>
            <div>
              <h3 className="text-3xl font-extrabold text-gray-900">{presentCount}</h3>
              <p className="text-sm text-gray-500">Present Today</p>
            </div>
          </div>
        </motion.div>
        <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
          <div className="bg-white p-6 rounded-3xl shadow-lg flex items-center space-x-4">
            <div className="p-3 rounded-full bg-red-100">
              <UserMinusIcon className="h-8 w-8 text-red-600" /> {/* Corrected Icon */}
            </div>
            <div>
              <h3 className="text-3xl font-extrabold text-gray-900">{absentCount}</h3>
              <p className="text-sm text-gray-500">Absent Today</p>
            </div>
          </div>
        </motion.div>
        <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.3 }}>
          <div className="bg-white p-6 rounded-3xl shadow-lg flex items-center space-x-4">
            <div className="p-3 rounded-full bg-yellow-100">
              <CalendarDaysIcon className="h-8 w-8 text-yellow-600" />
            </div>
            <div>
              <h3 className="text-3xl font-extrabold text-gray-900">{onLeaveCount}</h3>
              <p className="text-sm text-gray-500">On Leave</p>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
        {/* Attendance Summary & Punch-in Card */}
        <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.4 }} className="lg:col-span-1">
          <div className="bg-white p-6 rounded-3xl shadow-lg h-full flex flex-col items-center justify-center text-center">
            <ClockIcon className="h-16 w-16 text-purple-600 mb-4" />
            <h2 className="text-5xl font-extrabold text-gray-900 mb-2">
              {/* Using native JS formatting */}
              {formattedTime}
            </h2>
            <p className="text-lg text-gray-500 font-medium mb-4">
              {/* Using native JS formatting */}
              {formattedDate}
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-purple-600 text-white font-semibold py-3 px-8 rounded-full shadow-lg hover:bg-purple-700 transition-colors"
            >
              Check In
            </motion.button>
            <div className="w-48 mt-8">
              <Pie data={chartData} />
            </div>
          </div>
        </motion.div>

        {/* Attendance Table Section */}
        <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.5 }} className="lg:col-span-2">
          <div className="bg-white p-6 rounded-3xl shadow-lg h-full flex flex-col">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Today's Attendance Log</h3>
              <div className="flex space-x-2">
                <button className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" title="Export Data">
                  <ArrowDownTrayIcon className="h-5 w-5" />
                </button>
                <button className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" title="Manual Entry">
                  <PlusIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="relative mb-4">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-full bg-gray-100 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div className="overflow-x-auto flex-1">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Check In</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Check Out</th>
                    <th className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData.map((employee) => (
                    <tr key={employee.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{employee.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{employee.employeeId}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(employee.status)}`}>
                          {employee.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{employee.punchIn || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{employee.punchOut || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowap text-right text-sm font-medium">
                        <button className="text-purple-600 hover:text-purple-900" title="Edit Entry">
                          <AdjustmentsHorizontalIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AttendanceManagement;